import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { AppContext } from '../../../../appContext';
import { DashboardContext } from '../../DashboardContext';
import { getNotes, postNote, putNote, deleteNote } from '../../../../api/notesApi';
import { ConfirmationPopup } from '../../../../components/confirmationPopup/ConfirmationPopup';
import { addNoteReloadListener, removeNoteReloadListener, triggerNoteReload } from '../../../../utils/dataSyncTriggers';
import { getNotesCache, setNotesCache, updateNoteInCache, addNoteToCache, removeNoteFromCache } from '../../../../utils/notesCache';
import './Notes.css';
import TrashIcon from '../../../../assets/notesEditor/trash.svg';

interface INote {
    id?: string;
    title: string;
    text: string;
    date: Date;
    client?: string;
    tags?: string[];
}

export const Notes: React.FC = () => {
    console.log('Notes render start');
    const appContext = useContext(AppContext);
    console.log('AppContext:', appContext);
    const { translate } = appContext || {};
    console.log('translate function:', translate);
    
    const dashboardContext = useContext(DashboardContext);
    const { selectedClient } = dashboardContext || {};
    
    const [notes, setNotes] = useState<INote[]>([]);
    const [selectedNote, setSelectedNote] = useState<INote | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleInput, setTitleInput] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [boundToClient, setBoundToClient] = useState(false);
    
    const editorRef = useRef<HTMLDivElement>(null);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const currentNoteRef = useRef<INote | null>(null);
    const pendingNoteToSaveRef = useRef<INote | null>(null);
    const saveNoteRef = useRef<((note: INote) => Promise<void>) | null>(null);

    const loadNotes = useCallback(async () => {
        try {
            const cached = getNotesCache();
            if (cached && cached.length > 0) {
                setNotes(cached as INote[]);
                setLoading(false);
            } else {
                setLoading(true);
            }

            const response = await getNotes();
            const data = await response.json();
            
            const notesArray = data.map((noteObj: any) => {
                const noteId = Object.keys(noteObj)[0];
                const noteData = noteObj[noteId];
                return {
                    id: noteId,
                    title: noteData.title || 'Untitled',
                    text: noteData.text || '',
                    date: noteData.date ? new Date(noteData.date) : new Date(),
                    client: noteData.client,
                    tags: noteData.tags || []
                };
            });
            
            setNotesCache(notesArray);
            setNotes(notesArray);
            setError(null);
        } catch (err) {
            console.error('Failed to load notes:', err);
            setError(translate?.('notes_load_error') || 'Failed to load notes. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [translate]);

    useEffect(() => {
        console.log('Notes component mounted');
        loadNotes();
        
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
                saveTimeoutRef.current = null;
                if (pendingNoteToSaveRef.current) {
                    saveNoteRef.current?.(pendingNoteToSaveRef.current);
                }
            }
        };
    }, [loadNotes]);

    useEffect(() => {
        addNoteReloadListener(loadNotes);
        
        return () => {
            removeNoteReloadListener(loadNotes);
        };
    }, [loadNotes]);

    const createNewNote = async () => {
        const newNote: INote = {
            title: translate('notes_new_title'),
            text: '',
            date: new Date(),
            tags: [],
            client: boundToClient && selectedClient?.id ? selectedClient.id : undefined
        };

        try {
            const postResponse = await postNote({
                title: newNote.title,
                text: newNote.text,
                date: newNote.date,
                tags: newNote.tags,
                client: newNote.client
            });

            if (!postResponse || !postResponse.ok) return;

            // Server doesn't return ID in POST response — reload and find the new note
            const listResponse = await getNotes();
            if (!listResponse || !listResponse.ok) return;
            const listData = await listResponse.json();

            const parsedAll: INote[] = listData.map((noteObj: any) => {
                const id = Object.keys(noteObj)[0];
                return {
                    id,
                    title: noteObj[id].title || 'Untitled',
                    text: noteObj[id].text || '',
                    date: noteObj[id].date ? new Date(noteObj[id].date) : new Date(),
                    client: noteObj[id].client,
                    tags: noteObj[id].tags || []
                };
            });

            const matchingNotes = parsedAll.filter(n => n.title === newNote.title && n.client === newNote.client);
            const savedNote = matchingNotes.sort((a, b) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
            )[0];

            if (!savedNote || !savedNote.id) {
                setError(translate('notes_save_error'));
                return;
            }

            setNotesCache(parsedAll);
            setNotes(prev => {
                const withoutNew = prev.filter(n => n.id !== savedNote.id);
                return [savedNote, ...withoutNew];
            });
            addNoteToCache(savedNote);
            setSelectedNote(savedNote);
            setTitleInput(savedNote.title);

            if (editorRef.current) {
                editorRef.current.innerHTML = '';
            }
            triggerNoteReload();
        } catch (err) {
            console.error('Failed to create note:', err);
            setError(translate('notes_save_error'));
        }
    };

    const saveNote = useCallback(async (note: INote) => {
        if (!note.id) {
            console.error('Cannot save note without ID!');
            return;
        }
        
        const clientId = boundToClient && selectedClient?.id ? selectedClient.id : undefined;
        
        try {
            await putNote({
                title: note.title,
                text: note.text,
                date: note.date,
                tags: note.tags || [],
                client: clientId
            }, note.id);
            
            setNotes(prev => prev.map(n => n.id === note.id ? note : n));
            updateNoteInCache(note);
            pendingNoteToSaveRef.current = null;
        } catch (err) {
            console.error('Failed to save note with ID:', note.id, 'Error:', err);
            setError(translate('notes_save_error'));
        }
    }, [boundToClient, selectedClient?.id, translate]);

    const handleManualSave = async () => {
        if (!selectedNote || !editorRef.current) return;
        
        setIsSaving(true);
        
        try {
            const updatedNote = {
                ...selectedNote,
                text: editorRef.current.innerHTML || '',
                title: titleInput
            };
            
            await saveNote(updatedNote);
        } catch (err) {
            console.error('Error during manual save:', err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteNote = async () => {
        if (!selectedNote?.id) return;
        
        try {
            await deleteNote(selectedNote.id);
            setNotes(prev => prev.filter(n => n.id !== selectedNote.id));
            removeNoteFromCache(selectedNote.id);
            setSelectedNote(null);
            setShowDeleteConfirm(false);
            triggerNoteReload();
        } catch (err) {
            console.error('Failed to delete note:', err);
            setError(translate('notes_delete_error'));
            setShowDeleteConfirm(false);
        }
    };

    useEffect(() => {
        saveNoteRef.current = saveNote;
    }, [saveNote]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden' && pendingNoteToSaveRef.current && saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
                saveTimeoutRef.current = null;
                saveNoteRef.current?.(pendingNoteToSaveRef.current);
            }
        };

        const handleBeforeUnload = () => {
            if (pendingNoteToSaveRef.current && saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
                saveTimeoutRef.current = null;
                saveNoteRef.current?.(pendingNoteToSaveRef.current);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const handleNoteSelect = (note: INote) => {
        // Flush pending save before switching notes
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = null;
            if (pendingNoteToSaveRef.current) {
                saveNoteRef.current?.(pendingNoteToSaveRef.current);
                pendingNoteToSaveRef.current = null;
            }
        }
        
        setSelectedNote(note);
        setTitleInput(note.title);
        setBoundToClient(!!note.client);
        currentNoteRef.current = note;
    };
    
    // Sync editor content when selectedNote changes
    useEffect(() => {
        if (selectedNote && editorRef.current) {
            console.log('Syncing editor content for note:', selectedNote.id);
            editorRef.current.innerHTML = selectedNote.text || '';
        } else if (!selectedNote && editorRef.current) {
            editorRef.current.innerHTML = '';
        }
    }, [selectedNote?.id]);

    const handleEditorChange = () => {
        if (!selectedNote || !editorRef.current) return;
        
        const currentHTML = editorRef.current.innerHTML || '';
        
        const updatedNote = {
            ...selectedNote,
            text: currentHTML,
            title: titleInput
        };
        setSelectedNote(updatedNote);
        setNotes(prev => prev.map(n => n.id === selectedNote.id ? updatedNote : n));
        pendingNoteToSaveRef.current = updatedNote;
        
        // Auto-save with debounce (1.5 second delay)
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        
        saveTimeoutRef.current = setTimeout(() => {
            saveNote(updatedNote);
        }, 1500);
    };
    

    const handleTitleSave = () => {
        if (!selectedNote) return;
        
        const updatedNote = {
            ...selectedNote,
            title: titleInput || translate('notes_new_title')
        };
        
        saveNote(updatedNote);
        setSelectedNote(updatedNote);
        setEditingTitle(false);
    };

    const formatText = (command: string, value?: string) => {
        document.execCommand(command, false, value);
        editorRef.current?.focus();
    };

    console.log('Notes render return');
    
    if (!translate) {
        return <div className="Notes"><div>Error: translate function not available</div></div>;
    }
    
    return (
        <div className="Notes">
            {loading ? (
                <div className="Notes_loading">{translate('common_loading')}</div>
            ) : (
                <div className="Notes_content">
                    <div className="Notes_list">
                        <div className="Notes_list_header">
                            <div className="Notes_list_title">{translate('notes_title') || 'Notes'}</div>
                            <button className="button Notes_list_add_button" onClick={createNewNote} title={translate('notes_add')}>
                                <div className="Notes_list_add_plus"></div>
                            </button>
                        </div>
                        <div className="scroll Notes_list_items">
                            {notes.length === 0 ? (
                                <div className="Notes_empty">{translate('notes_empty')}</div>
                            ) : (
                                notes.map(note => (
                                    <div
                                        key={note.id}
                                        className={`Notes_item ${selectedNote?.id === note.id ? 'Notes_item_selected' : ''}`}
                                        onClick={() => handleNoteSelect(note)}
                                    >
                                        <div className="Notes_item_title">{note.title}</div>
                                        <div className="Notes_item_date">
                                            {new Date(note.date).toLocaleDateString()}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="Notes_editor">
                        {selectedNote ? (
                            <>
                                <div className="Notes_editor_header">
                                    <div className="Notes_editor_title">
                                        {editingTitle ? (
                                            <input
                                                type="text"
                                                value={titleInput}
                                                onChange={(e) => setTitleInput(e.target.value)}
                                                onBlur={handleTitleSave}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleTitleSave();
                                                }}
                                                autoFocus
                                                className="Notes_editor_title_input"
                                            />
                                        ) : (
                                            <h2 onClick={() => setEditingTitle(true)}>
                                                {selectedNote.title}
                                            </h2>
                                        )}
                                    </div>
                                    {selectedClient && (
                                        <label className="Notes_editor_bound_toggle">
                                            <input
                                                type="checkbox"
                                                checked={boundToClient}
                                                onChange={(e) => setBoundToClient(e.target.checked)}
                                            />
                                            <span>{translate('notes_bound_to_client')}</span>
                                        </label>
                                    )}
                                    <button 
                                        onClick={() => setShowDeleteConfirm(true)} 
                                        className="Notes_editor_delete" 
                                        title={translate('notes_delete')}
                                    >
                                        <img src={TrashIcon} alt={translate('notes_delete')} className="Notes_toolbar_icon" />
                                    </button>
                                </div>

                                <div className="Notes_toolbar">
                                    <button onClick={() => formatText('bold')} title="Bold">
                                        <strong>B</strong>
                                    </button>
                                    <button onClick={() => formatText('italic')} title="Italic">
                                        <em>I</em>
                                    </button>
                                    <button onClick={() => formatText('underline')} title="Underline">
                                        <u>U</u>
                                    </button>
                                    
                                    <div className="Notes_toolbar_separator"></div>
                                    
                                    <select onChange={(e) => formatText('fontSize', e.target.value)} defaultValue="3">
                                        <option value="1">Tiny</option>
                                        <option value="2">Small</option>
                                        <option value="3">Normal</option>
                                        <option value="4">Large</option>
                                        <option value="5">Huge</option>
                                    </select>
                                    
                                    <div className="Notes_toolbar_separator"></div>
                                    
                                    <button onClick={() => formatText('justifyLeft')} title="Align Left">
                                        <div className="Notes_align_icon Notes_align_left">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    </button>
                                    <button onClick={() => formatText('justifyCenter')} title="Align Center">
                                        <div className="Notes_align_icon Notes_align_center">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    </button>
                                    <button onClick={() => formatText('justifyRight')} title="Align Right">
                                        <div className="Notes_align_icon Notes_align_right">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    </button>
                                    
                                    <div className="Notes_toolbar_separator"></div>
                                    
                                    <input
                                        type="color"
                                        onChange={(e) => formatText('foreColor', e.target.value)}
                                        title="Text Color"
                                        className="Notes_toolbar_color"
                                    />
                                </div>

                                <div
                                    ref={editorRef}
                                    className="Notes_editor_content scroll"
                                    contentEditable
                                    onInput={handleEditorChange}
                                    suppressContentEditableWarning
                                />
                            </>
                        ) : (
                            <div className="Notes_empty_editor">
                                {translate('notes_select_or_create')}
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {showDeleteConfirm && (
                <ConfirmationPopup
                    message={translate('notes_confirm_delete') || 'Are you sure you want to delete this note? This action cannot be undone.'}
                    confirmText={translate('common_delete') || 'Delete'}
                    cancelText={translate('common_cancel') || 'Cancel'}
                    onConfirm={handleDeleteNote}
                    onCancel={() => setShowDeleteConfirm(false)}
                    confirmButtonStyle="danger"
                />
            )}
        </div>
    );
};
